import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { getByMcId, lineItemsApi, usersApi } from "./services/makerCheckerApi";
import { formatDateWithTime } from "../../utils/dateFormat";
import { Toast } from "../../shared/Toast";
import { buildComparisonGroups } from "./payload/buildComparisonGroups";
import ComparisonTable from "./payload/ComparisonTable";

const STATUS_LABELS = { 0: "Pending", 1: "Approved", 2: "Rejected" };
const ACTION_LABELS = { 1: "Add", 2: "Edit", 3: "Pause", 4: "Resume", 5: "Stop", 6: "Completed", 7: "Slot Status" };

// maker_checker_logs.rej_reason is VARCHAR(100) — enforce it here so a long reason is
// never silently truncated (or rejected) by the database.
const REJ_REASON_MAX = 100;

// Status is conveyed by label + icon as well as colour, so it never relies on colour alone.
const STATUS_STYLES = {
  0: { cls: "bg-amber-100 text-amber-800 ring-1 ring-amber-200", icon: "●" },
  1: { cls: "bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200", icon: "✓" },
  2: { cls: "bg-red-100 text-red-800 ring-1 ring-red-200", icon: "✕" },
};

const StatusBadge = ({ status }) => {
  const style = STATUS_STYLES[status] || { cls: "bg-gray-100 text-gray-700", icon: "?" };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-sm font-medium ${style.cls}`}>
      <span aria-hidden="true">{style.icon}</span>
      {STATUS_LABELS[status] ?? "Unknown"}
    </span>
  );
};

const Skeleton = () => (
  <div className="max-w-4xl mx-auto mt-6 p-4 animate-pulse" aria-busy="true" aria-live="polite">
    <span className="sr-only">Loading request…</span>
    <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
      <div className="h-6 bg-gray-200 rounded w-1/3 mb-4" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[...Array(4)].map((_, i) => <div key={i} className="h-4 bg-gray-100 rounded w-2/3" />)}
      </div>
    </div>
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="h-5 bg-gray-200 rounded w-1/4 mb-4" />
      {[...Array(3)].map((_, i) => <div key={i} className="h-10 bg-gray-100 rounded mb-2" />)}
    </div>
  </div>
);

const MakerCheckerDirectView = () => {
  const { mc_id } = useParams();

  const [action, setAction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [acting, setActing] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectInput, setShowRejectInput] = useState(false);
  const rejectRef = useRef(null);

  const load = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await getByMcId(mc_id);
      if (!data) throw new Error("Request not found");
      setAction(data);
    } catch (err) {
      console.error(err);
      setLoadError(err?.response?.data?.message || err?.message || "Failed to load maker-checker action");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!mc_id) return;
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mc_id]);

  // Move focus to the reason field when it opens, so keyboard users aren't stranded.
  useEffect(() => {
    if (showRejectInput) rejectRef.current?.focus();
  }, [showRejectInput]);

  // Works for both payload models — the dispatcher picks the right adapter.
  const groups = useMemo(() => buildComparisonGroups(action), [action]);

  const getApi = (refTableName) => (refTableName === "lineitems" ? lineItemsApi : usersApi);

  const handleApprove = async () => {
    if (!action || acting) return; // `acting` also guards against double-submit
    setActing(true);
    try {
      const result = await getApi(action.ref_table_name).approve(action.mc_id);
      if (result?.status === "SUCCESS") {
        Toast("success", result.message || "Approved successfully");
        setAction((prev) => ({ ...prev, status: 1 }));
      } else {
        Toast("error", result?.data?.message || "Failed to approve");
      }
    } catch (err) {
      Toast("error", err?.response?.data?.message || "Failed to approve");
    } finally {
      setActing(false);
    }
  };

  const handleReject = async () => {
    if (!action || acting) return;
    const reason = rejectReason.trim();
    if (!reason) {
      Toast("error", "Please enter a rejection reason");
      rejectRef.current?.focus();
      return;
    }
    setActing(true);
    try {
      const result = await getApi(action.ref_table_name).reject(action.mc_id, reason);
      if (result?.status === "SUCCESS") {
        Toast("info", `Rejected: ${reason}`);
        setAction((prev) => ({ ...prev, status: 2 }));
        setShowRejectInput(false);
      } else {
        Toast("error", result?.data?.message || "Failed to reject");
      }
    } catch (err) {
      Toast("error", err?.response?.data?.message || "Failed to reject");
    } finally {
      setActing(false);
    }
  };

  if (loading) return <Skeleton />;

  if (loadError) {
    return (
      <div className="max-w-4xl mx-auto mt-10 p-4">
        <div className="bg-white border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-600 font-medium mb-1">Couldn’t load this request</p>
          <p className="text-sm text-gray-500 mb-4">{loadError}</p>
          <button onClick={load} className="px-4 py-2 bg-gray-800 hover:bg-gray-900 text-white rounded-lg text-sm font-medium">
            Retry
          </button>
        </div>
      </div>
    );
  }

  const isPending = action.status === 0;
  const reasonTooLong = rejectReason.length > REJ_REASON_MAX;

  return (
    // pb-28 leaves room for the sticky mobile action bar so it never covers content.
    <div className="max-w-4xl mx-auto mt-6 p-4 pb-28 md:pb-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h1 className="text-lg sm:text-xl font-bold text-gray-800">Request #{action.mc_id}</h1>
          <StatusBadge status={action.status} />
        </div>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-gray-600">
          <div className="flex gap-2"><dt className="font-medium text-gray-700">Requested by:</dt><dd className="break-words">{action.user_name}</dd></div>
          <div className="flex gap-2"><dt className="font-medium text-gray-700">Action:</dt><dd>{ACTION_LABELS[action.action_type] ?? action.action_type}</dd></div>
          <div className="flex gap-2"><dt className="font-medium text-gray-700">Table:</dt><dd className="break-words">{action.ref_table_name || "—"}</dd></div>
          {action.ref_table_id != null && (
            <div className="flex gap-2"><dt className="font-medium text-gray-700">Record ID:</dt><dd>{action.ref_table_id}</dd></div>
          )}
          {action.time ? (
            <div className="flex gap-2"><dt className="font-medium text-gray-700">Created:</dt><dd>{formatDateWithTime(action.time)}</dd></div>
          ) : null}
        </dl>
      </div>

      {/* Comparison */}
      <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
        <h2 className="text-base sm:text-lg font-semibold text-gray-800 mb-4">
          {isPending ? "Requested changes" : "What changed"}
        </h2>
        <ComparisonTable
          groups={groups}
          emptyLabel={isPending ? "No differences found in this request." : "No field-level changes were recorded."}
        />
      </div>

      {/* Approve / Reject */}
      {isPending && (
        <>
          {showRejectInput && (
            <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
              <label htmlFor="reject-reason" className="block text-sm font-medium text-gray-700 mb-2">
                Rejection reason <span className="text-red-500">*</span>
              </label>
              <textarea
                id="reject-reason"
                ref={rejectRef}
                rows={3}
                maxLength={REJ_REASON_MAX}
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Escape") { setShowRejectInput(false); setRejectReason(""); } }}
                placeholder="Why is this being rejected?"
                aria-describedby="reject-counter"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-300"
              />
              <div id="reject-counter" className={`mt-1 text-xs ${reasonTooLong ? "text-red-600" : "text-gray-400"}`}>
                {rejectReason.length}/{REJ_REASON_MAX}
              </div>
            </div>
          )}

          {/* Sticky on mobile so approve/reject stays thumb-reachable; inline on desktop. */}
          <div className="fixed bottom-0 left-0 right-0 z-20 border-t border-gray-200 bg-white/95 backdrop-blur p-3
                          md:static md:border-0 md:bg-transparent md:p-0 md:backdrop-blur-none">
            <div className="max-w-4xl mx-auto flex gap-3">
              {!showRejectInput ? (
                <>
                  <button
                    disabled={acting}
                    onClick={handleApprove}
                    className="flex-1 md:flex-none px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {acting ? "Processing…" : "Approve"}
                  </button>
                  <button
                    disabled={acting}
                    onClick={() => setShowRejectInput(true)}
                    className="flex-1 md:flex-none px-5 py-2.5 bg-white border border-red-300 text-red-600 hover:bg-red-50 rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Reject
                  </button>
                </>
              ) : (
                <>
                  <button
                    disabled={acting || !rejectReason.trim() || reasonTooLong}
                    onClick={handleReject}
                    className="flex-1 md:flex-none px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {acting ? "Processing…" : "Confirm reject"}
                  </button>
                  <button
                    disabled={acting}
                    onClick={() => { setShowRejectInput(false); setRejectReason(""); }}
                    className="flex-1 md:flex-none px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium disabled:opacity-50 transition-colors"
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default MakerCheckerDirectView;
